import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, 
  Filter, 
  Eye, 
  MoreHorizontal,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  Briefcase,
  TrendingUp
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RiskBadge({ band, score }) {
  const config = {
    RED: { 
      gradient: "from-red-500/20 to-red-600/10",
      dot: "risk-dot-red",
      text: "text-red-400",
      border: "border-red-500/20"
    },
    AMBER: { 
      gradient: "from-amber-500/20 to-amber-600/10",
      dot: "risk-dot-amber",
      text: "text-amber-400",
      border: "border-amber-500/20"
    },
    GREEN: { 
      gradient: "from-emerald-500/20 to-emerald-600/10",
      dot: "risk-dot-green",
      text: "text-emerald-400",
      border: "border-emerald-500/20"
    }
  };
  const { gradient, dot, text, border } = config[band] || config.GREEN;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${gradient} border ${border}`}>
      <div className={`risk-dot ${dot}`} />
      <span className={`text-sm font-bold font-mono-data ${text}`}>{score}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    open: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
    in_progress: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
    resolved: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
    closed: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20" },
    sar_filed: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" }
  };
  const { icon: Icon, color, bg, border } = config[status] || config.open;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${bg} border ${border}`}>
      <Icon className={`h-3.5 w-3.5 ${color}`} />
      <span className={`text-xs font-medium capitalize ${color}`}>{status.replace('_', ' ')}</span>
    </div>
  );
}

export default function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bandFilter, setBandFilter] = useState("all");

  const fetchCases = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (bandFilter !== "all") params.append("band", bandFilter);
      
      const response = await axios.get(`${API}/cases?${params}`);
      setCases(response.data);
    } catch (error) {
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, bandFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const updateCaseStatus = async (caseId, newStatus) => {
    try {
      await axios.patch(`${API}/cases/${caseId}`, { status: newStatus });
      toast.success("Case status updated");
      fetchCases();
    } catch (error) {
      toast.error("Failed to update case");
    }
  };

  const filteredCases = cases.filter(c => 
    c.clientName?.toLowerCase().includes(search.toLowerCase()) ||
    c.caseId?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="cases-page">
      {/* Header Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        {[
          { label: 'Open Cases', value: cases.filter(c => c.status === 'open').length, color: 'amber' },
          { label: 'In Progress', value: cases.filter(c => c.status === 'in_progress').length, color: 'blue' },
          { label: 'Resolved', value: cases.filter(c => c.status === 'resolved').length, color: 'emerald' }
        ].map((stat, index) => (
          <div key={stat.label} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className={`p-3 rounded-lg bg-${stat.color}-500/10`}>
              <Briefcase className={`h-5 w-5 text-${stat.color}-400`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white font-mono-data">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="glass-card rounded-2xl border-0">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by client name or case ID..."
                  className="pl-11 input-premium h-11 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] input-premium h-11 rounded-xl" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="glass-card border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="sar_filed">SAR Filed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={bandFilter} onValueChange={setBandFilter}>
                <SelectTrigger className="w-full sm:w-[150px] input-premium h-11 rounded-xl" data-testid="filter-risk">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent className="glass-card border-white/10">
                  <SelectItem value="all">All Risk</SelectItem>
                  <SelectItem value="RED">High Risk</SelectItem>
                  <SelectItem value="AMBER">Medium Risk</SelectItem>
                  <SelectItem value="GREEN">Low Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Cases Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="glass-card rounded-2xl border-0 overflow-hidden">
          <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                <Briefcase className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Cases</h3>
                <p className="text-xs text-slate-500">{filteredCases.length} total cases</p>
              </div>
            </div>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <div className="inline-flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Loading cases...
                </div>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No cases found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Client</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Risk Score</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Priority</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">Created</TableHead>
                      <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <AnimatePresence>
                      {filteredCases.map((caseItem, index) => (
                        <motion.tr 
                          key={caseItem.caseId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.03 }}
                          className="table-row-premium cursor-pointer group"
                          onClick={() => navigate(`/cases/${caseItem.caseId}`)}
                          data-testid={`case-row-${caseItem.caseId}`}
                        >
                          <TableCell className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {caseItem.clientName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-white group-hover:text-blue-400 transition-colors">
                                  {caseItem.clientName}
                                </p>
                                <p className="text-xs text-slate-500 font-mono-data">
                                  #{caseItem.caseId.slice(0, 8)}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <RiskBadge band={caseItem.band} score={caseItem.riskScore} />
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={caseItem.status} />
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
                              caseItem.priority === 'high' 
                                ? 'bg-red-500/10 text-red-400 border border-red-500/20' 
                                : caseItem.priority === 'medium' 
                                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                                  : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                            }`}>
                              {caseItem.priority?.toUpperCase()}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm font-mono-data">
                            {formatDate(caseItem.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <ArrowUpRight className="h-4 w-4 text-slate-600 group-hover:text-blue-400 transition-colors" />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="glass-card border-white/10">
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); navigate(`/cases/${caseItem.caseId}`); }}
                                    className="cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); updateCaseStatus(caseItem.caseId, 'in_progress'); }}
                                    className="cursor-pointer"
                                  >
                                    Start Review
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); updateCaseStatus(caseItem.caseId, 'resolved'); }}
                                    className="cursor-pointer"
                                  >
                                    Mark Resolved
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); updateCaseStatus(caseItem.caseId, 'sar_filed'); }}
                                    className="cursor-pointer text-red-400"
                                  >
                                    File SAR
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
